import { useState, useEffect } from "react";
import { AppointmentData, fetchAppointments } from "@/services/appointment-service";
import { sendRebookingInvite } from "@/services/public-scheduling-service";
import { fetchDentists, Dentist } from "@/services/dentist-service";
import { useClinic } from "@/contexts/ClinicContext";
import { AppointmentFilters, AppointmentFiltersData } from "@/components/appointments/AppointmentFilters";
import { AppointmentsTable } from "@/components/appointments/AppointmentsTable";
import { EditAppointmentDialog } from "@/components/appointments/EditAppointmentDialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { WizardAgendamento } from "@/components/WizardAgendamento/WizardAgendamento";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Send, Hospital, Clipboard, RefreshCw } from "lucide-react";
import { formatISODate } from "@/utils/formatters";

const today = formatISODate(new Date());

const AppointmentsPage = () => {
  // ← Clínica global
  const { selectedClinicId, selectedClinic } = useClinic();

  const [appointments, setAppointments] = useState<AppointmentData[]>([]);
  const [dentists, setDentists] = useState<Dentist[]>([]);
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentData | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<AppointmentFiltersData>({ startDate: today, endDate: today });
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [originalAppointmentId, setOriginalAppointmentId] = useState<string | null>(null);
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  const [isRebookingInviteOpen, setIsRebookingInviteOpen] = useState(false);
  const [isSendingRebookingInvite, setIsSendingRebookingInvite] = useState(false);

  const { toast } = useToast();

  useEffect(() => {
    fetchDentists(false, false, undefined, 1, 100)
      .then(setDentists)
      .catch(() => toast({ title: "Erro ao carregar profissionais", variant: "destructive" }));
  }, []);

  // Re-carrega ao mudar clínica global, filtros ou página
  useEffect(() => { loadAppointments(); }, [filters, currentPage, selectedClinicId]);

  const loadAppointments = async () => {
    if (!selectedClinicId) {
      toast({ title: "Selecione uma clínica no topo da tela para carregar os atendimentos.", variant: "destructive" });
      return;
    }
    try {
      setLoading(true);
      const clinicId = filters.clinicId || selectedClinicId;
      const data = await fetchAppointments(
        true, false, clinicId, undefined, undefined, undefined,
        filters.dentistId, filters.startDate, filters.endDate,
        filters.appointmentStatus, filters.appointmentNumber,
        filters.patientName, filters.patientCPF, filters.patientPhone,
        currentPage, filters.pageSize || 100
      );
      setAppointments(data);
    } catch (error) {
      toast({ title: "Erro ao carregar agendamentos", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (appointment: AppointmentData) => {
    setSelectedAppointment(appointment);
    setDialogOpen(true);
  };

  const handleSave = (updatedAppointment: AppointmentData) => {
    setAppointments(prev => prev.map(apt => apt.id === updatedAppointment.id ? updatedAppointment : apt));
    toast({ title: "Sucesso!", description: "Agendamento atualizado com sucesso." });
  };

  const handleRebooking = (appointment: AppointmentData) => {
    const patientData = {
      id: appointment.patient_id,
      name: appointment.patient_name,
      cpf: appointment.patient_cpf,
      cellphone: appointment.patient_cellphone,
      dental_insurance_id: appointment.patient_dental_insurance_id || appointment.dental_insurance_id,
      card_number: appointment.patient_card_number || appointment.card_number
    };
    setSelectedPatient(patientData);
    setOriginalAppointmentId(appointment.id);
    setIsWizardOpen(true);
  };

  const handleOpenRebookingInviteDialog = (appointment: AppointmentData) => {
    setSelectedAppointment(appointment);
    setIsRebookingInviteOpen(true);
  };

  const handleSendRebookingInvite = async () => {
    if (!selectedAppointment) return;
    setIsSendingRebookingInvite(true);
    try {
      await sendRebookingInvite({
        clinic_id: selectedAppointment.clinic_id,
        cellphone: selectedAppointment.patient_cellphone,
        appointment_id: selectedAppointment.id,
        specialty_id: selectedAppointment.dental_specialty_id,
      });
      toast({ title: "Convite enviado!", description: `Convite de reagendamento enviado para ${selectedAppointment.patient_name}.` });
      setIsRebookingInviteOpen(false);
    } catch (error: any) {
      toast({ title: "Não foi possível enviar", description: error.message, variant: "destructive" });
    } finally {
      setIsSendingRebookingInvite(false);
    }
  };

  const cancelStatuses = ["canceled", "unbooked", "rescheduled", "not_attended"];
  const stats = {
    total: appointments.length,
    atendidos: appointments.filter(a => a.appointment_status === "attended").length,
    emAtendimento: appointments.filter(a => ["in_progress", "arrived"].includes(a.appointment_status)).length,
    confirmados: appointments.filter(a => a.appointment_status === "booked" && a.confirmed).length,
    naoConfirmados: appointments.filter(a => a.appointment_status === "booked" && !a.confirmed).length,
    cancelados: appointments.filter(a => cancelStatuses.includes(a.appointment_status)).length,
    faltaram: appointments.filter(a => a.appointment_status === "no_show").length,
  };

  return (
    <div className="page-wrapper">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Clipboard className="page-icon" />
            <h1 className="page-title">Atendimentos</h1>
            {selectedClinic && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-cyan-600 text-white shadow-sm">
                <Hospital className="h-3.5 w-3.5" />
                {selectedClinic.trade_name}
              </span>
            )}
          </div>
          <p className="page-subtitle">
            Gerencie os atendimentos e acompanhe o status de cada consulta.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={loadAppointments} disabled={loading} className="gap-1.5 h-7 text-xs">
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </div>

      {/* Resumo compacto */}
      <div className="flex flex-wrap gap-2">
        <StatBadge label="Total" value={stats.total} color="text-foreground" />
        <StatBadge label="Atendidos" value={stats.atendidos} color="text-emerald-700" />
        <StatBadge label="Em atendimento" value={stats.emAtendimento} color="text-green-700" />
        <StatBadge label="Confirmados" value={stats.confirmados} color="text-green-500" />
        <StatBadge label="Não confirmados" value={stats.naoConfirmados} color="text-yellow-600" />
        <StatBadge label="Cancelados" value={stats.cancelados} color="text-red-800" />
        <StatBadge label="Faltaram" value={stats.faltaram} color="text-red-500" />
      </div>

      {/* Filtros */}
      <AppointmentFilters
        dentists={dentists}
        onFilterChange={(f) => { setFilters(f); setCurrentPage(1); }}
      />

      {/* Tabela */}
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">{appointments.length} atendimento(s) encontrado(s)</p>
        {loading ? (
          <div className="bg-card border p-12 text-center rounded-lg text-muted-foreground">Carregando...</div>
        ) : (
          <>
            <AppointmentsTable
              appointments={appointments}
              onSendRebookingInvite={handleOpenRebookingInviteDialog}
              onRebooking={handleRebooking}
              onEdit={handleEdit}
              hideClinic={true}
              hideDentist={!!filters.dentistId && filters.dentistId !== 'all'}
            />
            <div className="flex items-center justify-between border rounded-lg px-4 py-2 bg-card">
              <span className="page-counter">Página {currentPage}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="btn-header" onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1}>Anterior</Button>
                <Button variant="outline" size="sm" className="btn-header" onClick={() => setCurrentPage(prev => prev + 1)} disabled={appointments.length < (filters.pageSize || 100)}>Próxima</Button>
              </div>
            </div>
          </>
        )}
      </div>

      <EditAppointmentDialog appointment={selectedAppointment} open={dialogOpen} onOpenChange={setDialogOpen} onSave={handleSave} />

      {isWizardOpen && selectedPatient && (
        <WizardAgendamento
          patient={selectedPatient}
          originalAppointmentId={originalAppointmentId || undefined}
          onClose={() => { setIsWizardOpen(false); setSelectedPatient(null); }}
          onSuccess={() => { loadAppointments(); }}
        />
      )}

      <AlertDialog open={isRebookingInviteOpen} onOpenChange={setIsRebookingInviteOpen}>
        <AlertDialogContent className="max-w-[420px]">
          <AlertDialogHeader>
            <div className="flex items-center gap-2 text-green-600 mb-2">
              <Send className="h-5 w-5" />
              <span className="font-bold uppercase tracking-wider text-xs">WhatsApp Business</span>
            </div>
            <AlertDialogTitle>Enviar convite de reagendamento?</AlertDialogTitle>
            <AlertDialogDescription>
              Será enviada uma mensagem para <strong>{selectedAppointment?.patient_name}</strong> perguntando se deseja escolher um novo horário pela conversa no WhatsApp.
              <br /><br />
              <span className="text-xs text-muted-foreground">
                Consulta atual: {selectedAppointment?.dental_specialty_name} · {selectedAppointment?.slot_date} {selectedAppointment?.slot_time}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSendingRebookingInvite}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={(e) => { e.preventDefault(); handleSendRebookingInvite(); }}
              disabled={isSendingRebookingInvite}
            >
              {isSendingRebookingInvite ? "Enviando..." : "Sim, enviar agora"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

const StatBadge = ({ label, value, color }: any) => (
  <div className="flex items-center gap-1.5 bg-card border border-border rounded-md px-3 py-1.5">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className={`text-sm font-bold ${color}`}>{value}</span>
  </div>
);


export default AppointmentsPage;